import theme from '@theme';
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';

import { Icon } from '../../icon';
import { ClipText } from '../../clip-text';
import { Attachment } from '@features/siteStatusV2/types';
import { Tooltip, OverlayTrigger } from 'react-bootstrap';

interface AttachmentItemProps {
  attachment: Attachment;
}

const renderTooltip = (props: { name: string }) => <Tooltip>{props.name}</Tooltip>;

const AttachmentItem: React.FC<AttachmentItemProps> = ({ attachment }) => {
  const maxLength = 10;
  const [isHovered, setIsHovered] = useState(false);
  const isTooltipShown = useMemo(
    () => isHovered && attachment.name.length > maxLength,
    [isHovered]
  );
  return (
    <AttachmentItemContainer key={attachment.name}>
      <InformationsContainer>
        <AttachmentType />
        <OverlayTrigger
          placement="top"
          show={isTooltipShown}
          delay={{ show: 250, hide: 400 }}
          overlay={renderTooltip({ name: attachment.name })}
        >
          <DisplayTitle
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <ClipText text={attachment.name} maxLength={maxLength} />
          </DisplayTitle>
        </OverlayTrigger>

        <CustomIconColorSwatch iconName="color-swatch" />
        <DisplaySize>
          <ClipText text={attachment.size} maxLength={maxLength} />
        </DisplaySize>
      </InformationsContainer>
      <CustomVerticalDot iconName="vertical-dots" />
    </AttachmentItemContainer>
  );
};

const AttachmentItemContainer = styled.div`
  display: flex;
  align-items: center;
  height: 1.8rem;
  justify-content: space-between;
  padding-left: 0.75rem;
  padding-right: 0.75rem;
  margin-bottom: 0.75rem;
  background-color: ${theme.palette.grey[50]};
  border-radius: 0.375rem;
`;

const InformationsContainer = styled.div`
  display: flex;
  align-items: center;
`;

const AttachmentType = styled.div`
  padding: 0.5rem;
  border-radius: 0.25rem;
  background-color: ${theme.palette.blue[100]};
  margin-right: 0.625rem;
`;

const DisplayTitle = styled.div`
  color: ${theme.palette.grey[700]};
  font-weight: 500;
  font-size: 0.75rem;
  line-height: 1rem;
  text-wrap: nowrap;
`;

const CustomIconColorSwatch = styled(Icon)`
  margin-left: 0.375rem;
  margin-right: 0.375rem;
  top: -1px;
  width: 0.5rem;
  color: ${theme.palette.grey[300]};
`;

const DisplaySize = styled.div`
  color: ${theme.palette.grey[400]};
  font-weight: 500;
  font-size: 0.6875rem;
  line-height: 0.75rem;
`;

const CustomVerticalDot = styled(Icon)`
  height: 1rem;
  color: ${theme.palette.blue[500]};
`;

export default AttachmentItem;
